import { FC, ReactElement, useState } from 'react';
import AppModal from '../app-modal/AppModal';
import { Alert, Button, Modal, Spinner } from 'react-bootstrap';
import i18next from 'i18next';
import { useAuthFetchFromRestAPI } from '../../util/api';

type PatientCompletionConversationModalProps = {
  show: boolean;
  patientId: string;
  onClose: () => void;
  onSave: () => void;
}

const PatientCompletionConversationModal: FC<PatientCompletionConversationModalProps> = ({
  show,
  patientId,
  onClose,
  onSave
}: PatientCompletionConversationModalProps): ReactElement => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { authFetch } = useAuthFetchFromRestAPI();

  const handleConfirm = async (): Promise<void> => {
    setErrorMessage(null);
    setIsSaving(true);

    try {
      await authFetch(`/api/v1/patients/completion-conversation/${patientId}`, { method: 'PATCH' });
      onSave();
    } catch (error: unknown) {
      console.error(error);
      setErrorMessage(error instanceof Error ? error.message : i18next.t('generic.unknownError'));
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleClose = (): void => {
    setErrorMessage(null);
    if (!isSaving) {
      onClose();
    }
  };


  return (
    <AppModal show={show} onHide={handleClose} backdrop="static" keyboard>
      <Modal.Header closeButton>
        <Modal.Title>{i18next.t('patientCompletionConversationModal.title')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {errorMessage && (
          <Alert variant="danger" className="mb-3">{errorMessage}</Alert>
        )}

        <p className="mb-0">
          {i18next.t('patientCompletionConversationModal.confirmText')}
        </p>

      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="secondary"
          type="button"
          onClick={handleClose}
          disabled={isSaving}
        >
          {i18next.t('generic.cancel')}
        </Button>
        <Button
          variant="primary"
          type="button"
          onClick={handleConfirm}
          disabled={isSaving}
        >
          {isSaving && (
            <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
          )}

          {i18next.t('patientCompletionConversationModal.confirm')}
        </Button>
      </Modal.Footer>
    </AppModal>
  );
};


export default PatientCompletionConversationModal;
